import React, { useState, useEffect, useRef } from 'react';
import { useNavigation } from '../contexts/NavigationProvider';
import { useUI } from '../contexts/UIProvider';
import { useAuth } from '../contexts/AuthProvider';
import * as api from '../services/apiService';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import { openAttachment } from '../utils/fileUtils';
import ArrowLeftIcon from './icons/ArrowLeftIcon';
import type { Band, EmbeddedMedia, Photo, RiderFile } from '../types';
import PlusCircleIcon from './icons/PlusCircleIcon';
import TrashIcon from './icons/TrashIcon';
import UserPlusIcon from './icons/UserPlusIcon';
import PhotoIcon from './icons/PhotoIcon';
import DocumentIcon from './icons/DocumentIcon';
import FacebookIcon from './icons/FacebookIcon';
import InstagramIcon from './icons/InstagramIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import CloseIcon from './icons/CloseIcon';
import CheckIcon from './icons/CheckIcon';

const COMMON_INSTRUMENTS = ['Vocals', 'Guitar', 'Bass', 'Drums', 'Keyboard', 'Piano', 'Saxophone', 'Trumpet', 'Violin', 'DJ', 'Percussion'];
const COMMON_GENRES = ['Rock', 'Pop', 'Jazz', 'Blues', 'Electronic', 'Hip-Hop', 'Classical', 'Folk', 'Metal', 'R&B', 'Soul', 'Funk'];

const getEmbedInfo = (url: string): { type: EmbeddedMedia['type'], embedUrl: string } => {
    if (!url || typeof url !== 'string' || !url.trim()) return { type: 'unknown', embedUrl: '' };
    try {
        const urlObj = new URL(url);
        if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
            const videoId = urlObj.hostname.includes('youtu.be') ? urlObj.pathname.slice(1) : urlObj.searchParams.get('v');
            if (videoId) return { type: 'youtube', embedUrl: `https://www.youtube.com/embed/${videoId}` };
        }
        if (urlObj.hostname.includes('spotify.com')) {
            const pathParts = urlObj.pathname.split('/');
            const trackId = pathParts[pathParts.length - 1];
            if (pathParts.includes('track') && trackId) return { type: 'spotify', embedUrl: `https://open.spotify.com/embed/track/${trackId}` };
        }
        if (urlObj.hostname.includes('soundcloud.com')) return { type: 'soundcloud', embedUrl: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23ff5500&auto_play=false` };
    } catch (e) {}
    return { type: 'unknown', embedUrl: '' };
};

const ManageBandPage: React.FC = () => {
    const { viewingBand, handleNavigation, refreshBands } = useNavigation();
    const { showToast } = useUI();
    const { currentUser } = useAuth();
    
    const [formData, setFormData] = useState<Partial<Band>>({
        members: [], photos: [], embeddedMedia: [], riderFiles: [], socialLinks: {}, genres: [], instruments: []
    });
    
    const [instrumentInput, setInstrumentInput] = useState('');
    const [genreInput, setGenreInput] = useState('');
    const [displayLocation, setDisplayLocation] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [isBrandingOpen, setIsBrandingOpen] = useState(true);
    const [newMemberHandle, setNewMemberHandle] = useState('');
    
    const bannerInputRef = useRef<HTMLInputElement>(null);
    const avatarInputRef = useRef<HTMLInputElement>(null);
    const photoInputRef = useRef<HTMLInputElement>(null);
    const riderFileInputRef = useRef<HTMLInputElement>(null);
    const cityInputRef = useRef<HTMLInputElement>(null);
    const autocompleteRef = useRef<any>(null);

    // Form initialization ref - prevents overwriting local edits with background context refreshes
    const lastInitializedId = useRef<string | null>(null);

    useEffect(() => { 
        if (viewingBand && viewingBand.id !== lastInitializedId.current) {
            lastInitializedId.current = viewingBand.id;
            setFormData({ 
                ...viewingBand,
                photos: viewingBand.photos || [],
                embeddedMedia: viewingBand.embeddedMedia || [],
                riderFiles: viewingBand.riderFiles || [],
                socialLinks: viewingBand.socialLinks || {},
                genres: viewingBand.genres || [],
                instruments: viewingBand.instruments || []
            }); 
            const fullLoc = [viewingBand.city, viewingBand.region, viewingBand.country].filter(Boolean).join(', ');
            setDisplayLocation(fullLoc);
        }
    }, [viewingBand]);

    useEffect(() => {
        const initMaps = async () => {
            const API_KEY = (window as any).GOOGLE_MAPS_API_KEY || "PASTE_YOUR_GOOGLE_MAPS_API_KEY_HERE";
            try {
                await loadGoogleMaps(API_KEY);
                if (!(window as any).google || !cityInputRef.current) return;
                
                if (autocompleteRef.current) return;

                autocompleteRef.current = new (window as any).google.maps.places.Autocomplete(cityInputRef.current, { 
                    fields: ['address_components', 'formatted_address', 'name']
                });

                autocompleteRef.current.addListener('place_changed', () => {
                    const place = autocompleteRef.current.getPlace();
                    if (place.address_components) {
                        let city = '';
                        let reg = '';
                        let cntry = '';
                        for (const component of place.address_components) {
                            const types = component.types;
                            if (types.includes('locality')) city = component.long_name;
                            if (types.includes('administrative_area_level_1')) reg = component.long_name;
                            if (types.includes('country')) cntry = component.long_name;
                        }
                        const newCity = city || place.name || '';
                        setFormData(prev => ({ ...prev, city: newCity, region: reg, country: cntry }));
                        setDisplayLocation([newCity, reg, cntry].filter(Boolean).join(', '));
                    }
                });
            } catch (err) {
                console.error("Maps failed to load", err);
            }
        };
        initMaps();
    }, []);
    
    if (!viewingBand) return <div className="p-8 text-center text-brand-muted font-urbanist">No project selected.</div>;

    const photosCount = formData.photos?.length || 0;
    const isPhotoLimitReached = photosCount >= 10;

    const handleSave = async () => {
        if (!formData.city) {
            showToast("Please select a verified city from the suggestions.");
            return;
        }
        setIsSaving(true);
        try {
            const updatedBand = await api.updateBand(viewingBand.id, { 
                bio: formData.bio, 
                city: formData.city,
                region: formData.region,
                country: formData.country,
                socialLinks: formData.socialLinks, 
                photos: formData.photos,
                bannerUrl: formData.bannerUrl,
                avatarUrl: formData.avatarUrl,
                embeddedMedia: formData.embeddedMedia,
                rider: formData.rider,
                riderFiles: formData.riderFiles,
                genres: formData.genres,
                instruments: formData.instruments
            });
            await refreshBands();
            showToast('Project updated!');
            handleNavigation('bandPage', { band: updatedBand });
        } catch (error) {
            showToast("Update failed.");
        } finally { setIsSaving(false); }
    };

    const handleLocationInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setDisplayLocation(val);
        // Clear structured data immediately when typing to force a re-selection
        setFormData(prev => ({ ...prev, city: '', region: '', country: '' }));
    };

    const clearLocation = () => {
        setDisplayLocation('');
        setFormData(prev => ({ ...prev, city: '', region: '', country: '' }));
        if (cityInputRef.current) cityInputRef.current.focus();
    };

    const handleAddMember = async () => {
        const cleanHandle = newMemberHandle.trim().replace('@', '');
        if (!cleanHandle) return;
        setIsAddingMember(true);
        try {
            await api.addBandMember(viewingBand.id, cleanHandle);
            await refreshBands();
            showToast("Member added!");
            setNewMemberHandle('');
        } catch (error) {
            showToast(error instanceof Error ? error.message : "Failed to add member.");
        } finally { setIsAddingMember(false); }
    };

    const handleRemoveMember = async (handle: string) => {
        if (!window.confirm(`Remove @${handle}?`)) return;
        try {
            await api.removeBandMember(viewingBand.id, handle);
            await refreshBands();
            showToast("Member removed.");
            if (handle === currentUser?.handle) handleNavigation('feed');
        } catch (error) { showToast("Removal failed."); }
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: 'banner' | 'avatar' | 'photo') => {
        if (e.target.files && e.target.files[0]) {
            if (field === 'photo' && isPhotoLimitReached) {
                showToast("Limit reached.");
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                const imageUrl = reader.result as string;
                if (field === 'banner') setFormData(prev => ({...prev, bannerUrl: imageUrl}));
                else if (field === 'avatar') setFormData(prev => ({...prev, avatarUrl: imageUrl}));
                else setFormData(prev => ({...prev, photos: [...(prev.photos || []), { id: Date.now().toString(), url: imageUrl }]}));
            };
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            const reader = new FileReader();
            reader.onloadend = () => {
                const fileUrl = reader.result as string;
                const newFile: RiderFile = {
                    id: Date.now().toString(),
                    name: file.name,
                    url: fileUrl
                };
                setFormData(prev => ({ ...prev, riderFiles: [...(prev.riderFiles || []), newFile] }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleAddInstrument = (val: string) => {
        const tag = val.trim();
        if (tag && !formData.instruments?.includes(tag)) {
            setFormData(prev => ({ ...prev, instruments: [...(prev.instruments || []), tag] }));
        }
        setInstrumentInput('');
    };

    const handleAddGenre = (val: string) => {
        const tag = val.trim();
        if (tag && !formData.genres?.includes(tag)) {
            setFormData(prev => ({ ...prev, genres: [...(prev.genres || []), tag] }));
        }
        setGenreInput('');
    };

    const removeInstrument = (tag: string) => {
        setFormData(prev => ({ ...prev, instruments: prev.instruments?.filter(i => i !== tag) }));
    };

    const removeGenre = (tag: string) => {
        setFormData(prev => ({ ...prev, genres: prev.genres?.filter(g => g !== tag) }));
    };

    return (
        <div className="space-y-6 font-urbanist pb-12 max-w-4xl mx-auto">
            <button onClick={() => handleNavigation('bandPage', { band: viewingBand })} className="flex items-center space-x-2 text-brand-muted hover:text-white font-semibold transition-colors">
                <ArrowLeftIcon className="h-5 w-5" /><span>Back to Page</span>
            </button>
            
            <div className="bg-brand-surface rounded-lg p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center border border-gray-800 gap-4 shadow-sm">
                <div>
                    <h2 className="text-3xl font-black text-white tracking-tight uppercase">Management Hub</h2>
                    <p className="text-xl text-brand-orange font-bold uppercase">@{viewingBand.name}</p>
                </div>
                <button onClick={handleSave} disabled={isSaving} className="w-full sm:w-auto bg-brand-orange text-white font-bold px-10 py-3 rounded-full hover:bg-orange-600 disabled:opacity-50 shadow-lg shadow-brand-orange/20 uppercase tracking-widest text-xs transition-all">
                    {isSaving ? 'Processing...' : 'Save Changes'}
                </button>
            </div>

            <section className="bg-brand-surface rounded-lg border border-gray-800 overflow-hidden shadow-sm">
                <button 
                    onClick={() => setIsBrandingOpen(!isBrandingOpen)}
                    className="w-full p-4 flex justify-between items-center hover:bg-gray-800/40 transition-colors"
                >
                    <div className="flex items-center gap-2">
                        <PhotoIcon className="h-5 w-5 text-brand-orange" />
                        <h3 className="text-lg font-black text-white uppercase tracking-widest">Project Branding</h3>
                    </div>
                    <ChevronDownIcon className={`h-5 w-5 text-brand-muted transition-transform duration-300 ${isBrandingOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isBrandingOpen && (
                    <div className="p-6 pt-0 border-t border-gray-800 animate-in slide-in-from-top-2 duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
                            <div className="flex flex-col items-center">
                                <label className="block text-[10px] font-extrabold text-brand-muted mb-3 uppercase tracking-widest w-full">Project Avatar</label>
                                <div className="relative group cursor-pointer" onClick={() => avatarInputRef.current?.click()}>
                                    <img src={formData.avatarUrl || 'https://picsum.photos/seed/default/200/200'} className="h-32 w-32 rounded-xl border-4 border-gray-700 group-hover:border-brand-orange transition-colors object-cover shadow-xl" alt="Band Avatar"/>
                                    <div className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                        <PhotoIcon className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                                <input type="file" ref={avatarInputRef} onChange={e => handleImageUpload(e, 'avatar')} accept="image/*" className="hidden"/>
                            </div>
                            <div>
                                <label className="block text-[10px] font-extrabold text-brand-muted mb-3 uppercase tracking-widest">Project Banner</label>
                                <div 
                                    className="h-32 rounded-lg bg-gray-700 bg-cover bg-center border border-gray-600 shadow-inner group relative cursor-pointer overflow-hidden" 
                                    style={{backgroundImage: `url(${formData.bannerUrl})`, backgroundSize: 'cover'}} 
                                    onClick={() => bannerInputRef.current?.click()}
                                >
                                     <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-[10px] font-bold uppercase tracking-widest text-white">Update Banner</div>
                                </div>
                                <input type="file" ref={bannerInputRef} onChange={e => handleImageUpload(e, 'banner')} accept="image/*" className="hidden"/>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <div className="space-y-6">
                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">Project Identity</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">Base City (Required - Select from suggestions)</label>
                            <div className="relative group">
                                <input 
                                    ref={cityInputRef} 
                                    type="text" 
                                    value={displayLocation} 
                                    onChange={handleLocationInputChange}
                                    placeholder="Start typing city..."
                                    className={`w-full bg-gray-700 text-white rounded-md p-3 outline-none border font-bold focus:border-brand-orange pr-20 ${!formData.city && displayLocation ? 'border-red-500' : 'border-gray-600'}`} 
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                                    {displayLocation && (
                                        <button 
                                            onClick={clearLocation}
                                            className="p-1 hover:bg-gray-600 rounded-full text-brand-muted hover:text-white transition-colors"
                                            title="Clear location"
                                        >
                                            <CloseIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                    {formData.city && <CheckIcon className="h-5 w-5 text-green-400" />}
                                </div>
                                {!formData.city && displayLocation && (
                                    <p className="text-[9px] text-red-400 mt-1 font-bold uppercase tracking-widest animate-pulse">Location not verified. Please select an option from the list.</p>
                                )}
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">Project Biography</label>
                            <textarea value={formData.bio || ''} onChange={(e) => setFormData(p => ({...p, bio: e.target.value}))} className="w-full bg-gray-700 text-white rounded-md p-4 outline-none resize-none leading-relaxed font-bold border border-gray-600 focus:border-brand-orange" rows={6} placeholder="Tell your project's story..."/>
                        </div>
                    </div>
                </section>

                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">Project Musical Profile</h3>
                    <div className="p-4 bg-gray-800/40 rounded-xl border border-gray-700 space-y-6">
                        <div>
                            <label className="block text-[10px] font-extrabold text-brand-orange mb-2 uppercase tracking-widest">Core Instruments (Type & Enter)</label>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {formData.instruments?.map(i => (
                                    <span key={i} className="text-[10px] bg-brand-orange/10 text-brand-orange px-2 py-1 rounded border border-brand-orange/20 font-bold flex items-center gap-1 group">
                                        #{i}
                                        <button onClick={() => removeInstrument(i)} className="hover:text-white transition-colors"><CloseIcon className="h-3 w-3" /></button>
                                    </span>
                                ))}
                            </div>
                            <input 
                                type="text" 
                                list="band-instruments"
                                value={instrumentInput}
                                onChange={(e) => setInstrumentInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddInstrument(instrumentInput);
                                    }
                                }}
                                placeholder="Add instrument..."
                                className="w-full bg-gray-900/60 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange text-sm" 
                            />
                            <datalist id="band-instruments">
                                {COMMON_INSTRUMENTS.map(i => <option key={i} value={i} />)}
                            </datalist>
                        </div>

                        <div>
                            <label className="block text-[10px] font-extrabold text-blue-400 mb-2 uppercase tracking-widest">Main Genres (Type & Enter)</label>
                            <div className="flex flex-wrap gap-2 mb-3">
                                {formData.genres?.map(g => (
                                    <span key={g} className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-1 rounded border border-blue-400/20 font-bold flex items-center gap-1 group">
                                        #{g}
                                        <button onClick={() => removeGenre(g)} className="hover:text-white transition-colors"><CloseIcon className="h-3 w-3" /></button>
                                    </span>
                                ))}
                            </div>
                            <input 
                                type="text" 
                                list="band-genres"
                                value={genreInput}
                                onChange={(e) => setGenreInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddGenre(genreInput);
                                    }
                                }}
                                placeholder="Add genre..."
                                className="w-full bg-gray-900/60 text-white rounded-md p-3 outline-none border border-gray-600 font-bold focus:border-brand-orange text-sm" 
                            />
                            <datalist id="band-genres">
                                {COMMON_GENRES.map(g => <option key={g} value={g} />)}
                            </datalist>
                        </div>
                    </div>
                </section>

                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">Project Socials</h3>
                    <div className="space-y-4">
                        <div className="flex flex-col gap-2">
                            <label className="text-[10px] font-extrabold text-brand-muted uppercase tracking-widest">Facebook Project Page</label>
                            <div className="flex items-center gap-3 bg-gray-700/40 rounded-md border border-gray-600 p-1 pr-3 focus-within:border-brand-orange transition-colors">
                                <div className="bg-[#1877F2]/10 p-2 rounded-md">
                                    <FacebookIcon className="h-5 w-5 text-[#1877F2]" />
                                </div>
                                <input 
                                    type="url" 
                                    value={formData.socialLinks?.facebook || ''} 
                                    onChange={(e) => setFormData({...formData, socialLinks: {...formData.socialLinks, facebook: e.target.value}})} 
                                    placeholder="https://facebook.com/project-page" 
                                    className="flex-1 bg-transparent text-white outline-none font-bold text-sm" 
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <label className="text-[10px] font-extrabold text-brand-muted uppercase tracking-widest">Instagram Project Page</label>
                            <div className="flex items-center gap-3 bg-gray-700/40 rounded-md border border-gray-600 p-1 pr-3 focus-within:border-brand-orange transition-colors">
                                <div className="bg-[#E4405F]/10 p-2 rounded-md">
                                    <InstagramIcon className="h-5 w-5 text-[#E4405F]" />
                                </div>
                                <input 
                                    type="url" 
                                    value={formData.socialLinks?.instagram || ''} 
                                    onChange={(e) => setFormData({...formData, socialLinks: {...formData.socialLinks, instagram: e.target.value}})} 
                                    placeholder="https://instagram.com/project-page" 
                                    className="flex-1 bg-transparent text-white outline-none font-bold text-sm" 
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <h3 className="text-lg font-black text-white border-b border-gray-800 pb-2 uppercase tracking-widest">Media Showcase</h3>
                    <div className="space-y-4">
                        {(formData.embeddedMedia || []).map((item, idx) => (
                            <div key={item.id || idx} className="flex items-center gap-3">
                                <input value={item.url} onChange={e => { const n = [...formData.embeddedMedia!]; n[idx] = {...item, url: e.target.value, type: getEmbedInfo(e.target.value).type}; setFormData({...formData, embeddedMedia: n}); }} className="flex-1 bg-gray-700 p-3 rounded text-sm font-bold border border-gray-600 focus:border-brand-orange" placeholder="Video or Audio URL (YouTube, Spotify, SoundCloud)"/>
                                <button onClick={() => setFormData({...formData, embeddedMedia: formData.embeddedMedia?.filter((_, i) => i !== idx)})} className="p-2 text-red-400 hover:bg-red-400/10 rounded-full transition-colors"><TrashIcon className="h-5 w-5"/></button>
                            </div>
                        ))}
                        <button onClick={() => setFormData({...formData, embeddedMedia: [...(formData.embeddedMedia || []), { id: Date.now().toString(), type: 'unknown', url: '' }]})} className="text-xs text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline"><PlusCircleIcon className="h-4 w-4"/> Add Track</button>
                    </div>
                </section>

                <section className="bg-brand-surface rounded-lg p-6 border border-gray-800 shadow-sm">
                    <h3 className="text-lg font-black text-white mb-4 uppercase tracking-widest">Roster Management</h3>
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-orange font-bold">@</span>
                                <input value={newMemberHandle} onChange={e => setNewMemberHandle(e.target.value)} placeholder="handle" className="w-full bg-gray-700 rounded-lg p-2 pl-7 text-sm font-bold border border-gray-600 outline-none focus:border-brand-orange" />
                            </div>
                            <button onClick={handleAddMember} disabled={isAddingMember || !newMemberHandle.trim()} className="bg-brand-orange p-2 rounded-lg text-white hover:bg-orange-600 transition-colors disabled:opacity-50">
                                {isAddingMember ? <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <UserPlusIcon className="h-5 w-5"/>}
                            </button>
                        </div>
                        <ul className="space-y-3 pt-2">
                            {(formData.members || []).map(m => (
                                <li key={m.handle} className="flex items-center justify-between bg-gray-800/40 p-2 rounded-lg border border-gray-700 hover:bg-gray-800/60 transition-colors">
                                    <div className="flex items-center gap-2">
                                        <img src={m.avatarUrl} className="h-8 w-8 rounded-full border border-gray-600" alt=""/>
                                        <span className="text-xs font-bold text-white truncate max-w-[120px]">{m.name}</span>
                                    </div>
                                    <button onClick={() => handleRemoveMember(m.handle)} className="text-red-400 hover:text-red-300 p-1 transition-colors"><TrashIcon className="h-4 w-4"/></button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                        <div className="flex items-center gap-2">
                            <h3 className="text-lg font-black text-white uppercase tracking-widest">Photo Gallery</h3>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isPhotoLimitReached ? 'bg-red-500/20 text-red-400' : 'bg-brand-orange/10 text-brand-orange'}`}>
                                {photosCount}/10
                            </span>
                        </div>
                        {!isPhotoLimitReached && (
                            <button onClick={() => photoInputRef.current?.click()} className="text-[10px] text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline"><PlusCircleIcon className="h-4 w-4"/> Add Photo</button>
                        )}
                        <input type="file" ref={photoInputRef} onChange={e => handleImageUpload(e, 'photo')} accept="image/*" className="hidden"/>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {(formData.photos || []).map((photo: Photo) => (
                            <div key={photo.id} className="relative aspect-square group">
                                <img src={photo.url} className="w-full h-full object-cover rounded-lg border border-gray-800" alt="Gallery"/>
                                <button onClick={() => setFormData(p => ({...p, photos: p.photos?.filter(ph => ph.id !== photo.id)}))} className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"><TrashIcon className="h-4 w-4"/></button>
                            </div>
                        ))}
                    </div>
                </section>

                <section className="bg-brand-surface rounded-lg p-6 space-y-6 border border-gray-800 shadow-sm">
                    <div className="flex justify-between items-center border-b border-gray-800 pb-2">
                        <h3 className="text-lg font-black text-white uppercase tracking-widest">Rider & Technical Specs</h3>
                    </div>
                    <div>
                        <label className="block text-[10px] font-extrabold text-brand-muted mb-2 uppercase tracking-widest">Project Technical Rider</label>
                        <textarea 
                            value={formData.rider || ''} 
                            onChange={(e) => setFormData(p => ({...p, rider: e.target.value}))} 
                            className="w-full bg-gray-700 text-white rounded-md p-4 outline-none resize-none leading-relaxed font-bold border border-gray-600 focus:border-brand-orange" 
                            rows={4} 
                            placeholder="Stage setup, backline requirements, sound & light specs..."
                        />
                    </div>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <label className="block text-[10px] font-extrabold text-brand-muted uppercase tracking-widest">Documents / Plots (PDF/JPG)</label>
                            <button onClick={() => riderFileInputRef.current?.click()} className="text-[10px] text-brand-orange font-bold uppercase tracking-widest flex items-center gap-2 hover:underline">
                                <PlusCircleIcon className="h-4 w-4"/> Add File
                            </button>
                            <input type="file" ref={riderFileInputRef} onChange={handleFileUpload} accept=".pdf,image/*" className="hidden"/>
                        </div>
                        <ul className="space-y-2">
                            {(formData.riderFiles || []).map((file: RiderFile) => (
                                <li key={file.id} className="flex items-center justify-between bg-gray-800/40 p-3 rounded-lg border border-gray-700">
                                    <button 
                                        onClick={() => openAttachment(file)}
                                        className="flex items-center gap-3 flex-1 text-left hover:opacity-80 transition-opacity"
                                    >
                                        <DocumentIcon className="h-5 w-5 text-brand-orange"/>
                                        <span className="text-sm font-bold text-white truncate max-w-[200px]">{file.name}</span>
                                    </button>
                                    <button onClick={() => setFormData(p => ({...p, riderFiles: p.riderFiles?.filter(f => f.id !== file.id)}))} className="text-red-400 hover:text-red-300 p-1">
                                        <TrashIcon className="h-5 w-5"/>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default ManageBandPage;